/**
 * @file vendor-finance-details.tsx
 * @deprecated Copied to app/modules/procurement/pages/vendors_management/payments/vendor-finance-details.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/vendors_management/payments/vendor-finance-details
 * @description Page component for managing vendor finance details in the vendors management section.
 *   Displays a list of vendor finance details with pagination, search functionality, and CRUD operations.
 *   Allows users to view the details of vendor finance details with warehouse context integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Remove debug console.log calls (localStorage businessId, selected, selectedData).
 * @todo Warehouse refetch on selectedWarehouse is implemented — verify payout APIs honor warehouse param.
 */

import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Nav, Row, Tab } from "react-bootstrap";
import { CustomSelect } from "../../../../custom/Select/CustomSelect";
import { useLocation } from "react-router-dom";
import {
  Accepted,
  Add,
  All,
  AllSeller,
  BankPayment,
  Credit,
  NewRequests,
  OrdersDelivery,
  PAGE_LIMIT,
  PaymentToSellers,
  PermissionModule,
  Permissions,
  Rejected,
  SellerPaymentPaid,
  SellerPaymentUnPaid,
  TigoPesaPayment,
  UnderReview,
  View,
} from "../../../../../utils/constants";
import APICallService from "../../../../../api/apiCallService";
import { vendor } from "../../../../../api/apiEndPoints";
import { sellerJSON } from "../../../../../api/apiJSON/seller";
import VerifiedPro from "../../../../../_admin/assets/media/svg_uTrade/verified.svg";
import Loader from "../../../../../Global/loader";
import Method from "../../../../../utils/methods";
import { useAuth } from "../../../auth";
import { paymentStatusJSON } from "../../../../../utils/staticJSON";
import Pagination from "../../../../../Global/pagination";
import {
  interTransferList,
  listVendorAdvertisement,
  listVendorFinanceDetails,
  listVendorGoodsRequest,
  listVendorHistoryOfInventory,
  listVendorInventory,
  listVendorProfileCatalogue,
  listVendorStockDetails,
  VendorProfile,
} from "../../../../../utils/storeString";
import {
  getKey,
  removeAllKeys,
  removeKey,
  setKey,
} from "../../../../../Global/history";
import { ElementAnimateUtil } from "../../../../../_admin/assets/ts/_utils";
import PermissionModal from "../../../../modals/permission";
import { ColumnDef } from "@tanstack/react-table";
import { CustomTable } from "../../../../custom/data-table";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
import { error as errorToast } from "../../../../../Global/toast";
import { mixesGroups, poQuantityText } from "../../vendor-ledger/supplier-ledger-groups";
const VendorFinanceDetails = () => {
  const { state }: any = useLocation();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const [active, setActive] = useState(
    getKey(listVendorFinanceDetails.currentTab)
      ? () => {
          let temp: any = getKey(listVendorFinanceDetails.currentTab);
          return parseInt(temp);
        }
      : 1
  );
  const [defaultValue, setDefaultValue] = useState("1");
  const {navigateTo} = useNavigationHistory();
  const handleSelect = (event: any) => {
    setDefaultValue(event);
  };
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [search, setSearch] = useState<string>("");
  const [selected, setSelected] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<any>([]);
  const [selectedData, setSelectedData] = useState<any>([]);
  // supplier group filter for the PO list ("" = all, "none" = POs of the supplier itself)
  const groupFilterRef = useRef<string>("");
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [groupOptions, setGroupOptions] = useState<{ reference: string; name: string }[]>([]);
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState<any>(
    getKey(listVendorFinanceDetails.page) || 1
  );
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listVendorFinanceDetails.limit) || PAGE_LIMIT
  );
  const [businessDetails, setBusinessDetails] = useState<any>();
  const [paymentState, setPaymentState] = useState<any>(
    getKey(listVendorFinanceDetails.filter)
      ? getKey(listVendorFinanceDetails.filter)
      : paymentStatusJSON[0]
  );
  const [transferPaymentState, setTransferPaymentState] = useState<any>(
    getKey(interTransferList.filter)
      ? getKey(interTransferList.filter)
      : paymentStatusJSON[0]
  );
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showOrderPermissionModal, setShowOrderPermissionModal] =
    useState(false);
  const [transferData, setTransferData] = useState<any>([]);

  const canViewOrderDetails =
    Method.hasPermission(PermissionModule.PaymentToVendors, View, currentUser) &&
    Method.hasPermission(PermissionModule.OrdersDelivery, View, currentUser);
  const canViewPaymentDetails = Method.hasPermission(
    PermissionModule.PaymentToVendors,
    View,
    currentUser
  );

  const goToOrderDetails = (paymentVal: any) => {
    let currentScrollY = window.scrollY;
    setKey(listVendorFinanceDetails.scrollPosition, currentScrollY);
    localStorage.setItem("businessId", state?.id);
    navigateTo("/UProcure/purchase-order-management/purchase-order-details", {
      state: { id: paymentVal?._id },
    });
  };

  const goToPaymentDetails = (paymentVal: any) => {
    let currentScrollY = window.scrollY;
    setKey(listVendorFinanceDetails.scrollPosition, currentScrollY);
    localStorage.setItem("businessId", state?.id);
    navigateTo("payment-completed", {
      state: {
        businessDetails,
        paymentVal,
        moduleName: paymentVal.isOrder ? "order" : "interTransfer",
      },
    });
  };

  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(PermissionModule.PaymentToVendors,currentUser)) {
        return window.history.back();
      }
      setFetchLoading(true);
      setPage(1);
      setKey(listVendorFinanceDetails.page, 1);
      localStorage.removeItem("profile");
      if (!localStorage.getItem("businessId")) {
        localStorage.setItem("businessId", state.id);
        localStorage.setItem("details", JSON.stringify(state));
      }
      if (getKey(listVendorFinanceDetails.currentTab)) {
        let id: any = localStorage.getItem("businessId");
        let temp: any = localStorage.getItem("details");
        let details: any = JSON.parse(temp);
        let tempTab: any = getKey(listVendorFinanceDetails.currentTab);
        if (parseInt(tempTab) === 2) {
          if (localStorage.getItem("businessId")) {
            await fetchPayment(1, pageLimit, id, search);
          } else {
            localStorage.setItem("businessId", state.id);
            localStorage.setItem("details", JSON.stringify(state));
            await fetchPayment(1, pageLimit, state.id, search);
          }
          await setBusinessDetails(state);
        } else if (parseInt(tempTab) === 3) {
          let filter: any = paymentStatusJSON[0];
          if (getKey(interTransferList.filter)) {
            filter = getKey(interTransferList.filter);
          }
          if (localStorage.getItem("businessId")) {
            await fetchTransferRequest(1, pageLimit, id, filter.value);
          } else {
            localStorage.setItem("businessId", state.id);
            localStorage.setItem("details", JSON.stringify(state));
            await fetchTransferRequest(1, pageLimit, state.id, filter.value);
          }
          await setBusinessDetails(state);
        } else {
          let filter: any = paymentStatusJSON[0];
          if (getKey(listVendorFinanceDetails.filter)) {
            filter = getKey(listVendorFinanceDetails.filter);
          }
          if (localStorage.getItem("businessId")) {
            await fetchOrder(1, pageLimit, id, search, filter.value);
            await setBusinessDetails(details);
          } else {
            localStorage.setItem("businessId", state.id);
            localStorage.setItem("details", JSON.stringify(state));
            await fetchOrder(1, pageLimit, state.id, search, filter.value);
            await setBusinessDetails(state);
          }
        }
      } else {
        let filter: any = paymentStatusJSON[0];
        if (getKey(listVendorFinanceDetails.filter)) {
          filter = getKey(listVendorFinanceDetails.filter);
        }
        if (localStorage.getItem("businessId")) {
          let id: any = localStorage.getItem("businessId");
          let temp: any = localStorage.getItem("details");
          let details: any = JSON.parse(temp);
          await fetchOrder(1, pageLimit, id, search, filter.value);
          await setBusinessDetails(details);
        } else {
          localStorage.setItem("businessId", state.id);
          localStorage.setItem("details", JSON.stringify(state));
          await fetchOrder(1, pageLimit, state.id, search, filter.value);
          await setBusinessDetails(state);
        }
      }
      setDataFetched(true);
      setFetchLoading(false);
    })();
  }, [selectedWarehouse?.value]);
  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(
        listVendorFinanceDetails.scrollPosition
      );
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
      }
      removeKey(VendorProfile.currentTab);
      removeAllKeys(listVendorProfileCatalogue);
      removeAllKeys(listVendorGoodsRequest);
      removeAllKeys(listVendorAdvertisement);
      removeAllKeys(listVendorInventory);
      removeAllKeys(listVendorHistoryOfInventory);
      removeKey(listVendorStockDetails.search);
    }
  }, [dataFetched]);
  useEffect(() => {
    let temp = { ...paymentState };
    if (temp.value === SellerPaymentPaid) {
      setSelectedId([]);
      setSelected(!selected);
      setSelectLoading(false);
      temp.label = "Paid" + (totalRecords ? " (" + totalRecords + ")" : "");
    } else if (temp.value === SellerPaymentUnPaid) {
      temp.label = "Not paid" + (totalRecords ? " (" + totalRecords + ")" : "");
    } else {
      setSelectedId([]);
      setSelected(!selected);
      setSelectLoading(false);
      temp.label =
        "All orders" + (totalRecords ? " (" + totalRecords + ")" : "");
    }
    setKey(listVendorFinanceDetails.filter, temp, false);
    setPaymentState(temp);
  }, [totalRecords]);
  const handleActive = async (event: any) => {
    setActive(event);
    setSelectedData([]);
    setSelectedId([]);
    setSelected(!selected);
    setKey(listVendorFinanceDetails.currentTab, parseInt(event));
    setKey(listVendorFinanceDetails.page, 1);
    setFetchLoading(true);
    setTotalRecords(0);
    setPaymentData([]);
    setPage(1);
    if (parseInt(event) === 2) {
      await fetchPayment(1, pageLimit, businessDetails.id, search);
    } else if (parseInt(event) === 3) {
      await fetchTransferRequest(
        1,
        pageLimit,
        businessDetails.id,
        transferPaymentState.value
      );
    } else {
      await fetchOrder(
        1,
        pageLimit,
        businessDetails?.id,
        search,
        paymentState.value
      );
    }
    setFetchLoading(false);
  };
  const handleChange = async (event: any) => {
    setPaymentState(event);
    setKey(listVendorFinanceDetails.filter, event, false);
    setKey(listVendorFinanceDetails.page, 1);
    setPage(1);
    setTotalRecords(0);
    setSelected(false);
    setPaymentData([]);
    setSelectedId([]);
    setSelectedData([]);
    setLoading(true);
    await fetchOrder(1, pageLimit, businessDetails.id, search, event.value);
    setLoading(false);
  };
  const handleGroupFilter = async (event: { value: string }) => {
    groupFilterRef.current = event?.value || "";
    setGroupFilter(groupFilterRef.current);
    setPage(1);
    setTotalRecords(0);
    setSelected(false);
    setSelectedId([]);
    setSelectedData([]);
    setLoading(true);
    await fetchOrder(1, pageLimit, businessDetails.id, search, paymentState.value);
    setLoading(false);
  };
  const groupSelectOptions = [
    { value: "", label: "All groups" },
    ...groupOptions.map((g) => ({ value: g.reference, label: g.name })),
    { value: "none", label: "No group (supplier)" },
  ];
  const handleChangeTransferFilter = async (event: any) => {
    setTransferPaymentState(event);
    setKey(interTransferList.filter, event, false);
    setKey(interTransferList.page, 1);
    setPage(1);
    setTotalRecords(0);
    setSelected(false);
    setPaymentData([]);
    setSelectedId([]);
    setSelectedData([]);
    setLoading(true);
    await fetchTransferRequest(1, pageLimit, businessDetails.id, event.value);
    setLoading(false);
  };
  const fetchPayment = async (
    pageNo: number,
    limit: number,
    businessId: string,
    searchValue: string
  ) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: searchValue,
    };

    let apiService = new APICallService(
      vendor.payoutList,
      params,
      { id: businessId },
      "",
      "",
      "",
      PaymentToSellers
    );
    let response = await apiService.callAPI();
    if (response) {
      if (response.total) {
        setTotalRecords(response.total);
      }
      setPaymentData(response.records);
    }
  };
  const fetchOrder = async (
    pageNo: number,
    limit: number,
    businessId: string,
    searchValue: string,
    state: number,
    selectAll: boolean = false
  ) => {
    let params: any = {
      pageNo: pageNo,
      limit: selectAll ? 0 : limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: searchValue,
      state: state,
    };
    let apiService = new APICallService(
      vendor.payoutOrderList,
      {
        ...sellerJSON.listPayoutOrders(params),
        ...(groupFilterRef.current ? { supplierGroup: groupFilterRef.current } : {}),
      },
      { id: businessId },
      "",
      "",
      "",
      PaymentToSellers
    );
    let response = await apiService.callAPI();
    if (Array.isArray(response?.supplierGroups)) setGroupOptions(response.supplierGroups);
    if (response) {
      if (response.total) {
        setTotalRecords(response.total);
      }
      if (selectAll) {
        let temp: any = [];
        if (response.records) {
          response.records?.map((val: any) => {
            temp.push(val._id);
          });
        }
        setSelectedData(response.records);
        setSelectedId(temp);
      }
      setPaymentData(response.records);
    }
  };
  const fetchTransferRequest = async (
    pageNo: number,
    limit: number,
    businessId: string,
    state: number,
    selectAll: boolean = false
  ) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      state: state,
    };
    // let apiService = new APICallService(
    //   interTransferRequest.buisnessPayout,
    //   params,
    //   { id: businessId },
    //   "",
    //   "",
    //   "",
    //   AllModules,

    // );
    // let response = await apiService.callAPI();
    // if (response) {
    //   if (response.total) {
    //     setTotalRecords(response.total);
    //   }
    //   if (selectAll) {
    //     let temp: any = [];
    //     if (response.records) {
    //       response.records.map((val: any) => {
    //         temp.push(val._id);
    //       });
    //     }
    //     setSelectedData(response.records);
    //     setSelectedId(temp);
    //   }
    //   setTransferData(response.records);
    // }
  };
  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setLoading(true);
    setKey(listVendorFinanceDetails.page, val);
    if (active === 1) {
      await fetchOrder(
        val,
        pageLimit,
        businessDetails.id,
        search,
        paymentState.value,
        false
      );
    } else if (active === 3) {
      await fetchTransferRequest(
        val,
        pageLimit,
        businessDetails.id,
        transferPaymentState.value,
        false
      );
    } else {
      await fetchPayment(val, pageLimit, businessDetails.id, search);
    }
    setLoading(false);
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setLoading(true);
    setKey(listVendorFinanceDetails.page, val + 1);
    if (active === 1) {
      await fetchOrder(
        val + 1,
        pageLimit,
        businessDetails.id,
        search,
        paymentState.value,
        false
      );
    } else if (active === 3) {
      await fetchTransferRequest(
        val + 1,
        pageLimit,
        businessDetails.id,
        transferPaymentState.value,
        false
      );
    } else {
      await fetchPayment(val + 1, pageLimit, businessDetails.id, search);
    }
    setLoading(false);
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setLoading(true);
    setKey(listVendorFinanceDetails.page, val - 1);
    if (active === 1) {
      await fetchOrder(
        val - 1,
        pageLimit,
        businessDetails.id,
        search,
        paymentState.value,
        false
      );
    } else if (active === 3) {
      await fetchTransferRequest(
        val - 1,
        pageLimit,
        businessDetails.id,
        transferPaymentState.value,
        false
      );
    } else {
      await fetchPayment(val - 1, pageLimit, businessDetails.id, search);
    }
    setLoading(false);
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    await setPageLimit(parseInt(event.target.value));
    setKey(listVendorFinanceDetails.page, 1);
    setKey(listVendorFinanceDetails.limit, parseInt(event.target.value));
    setLoading(true);
    if (active === 1) {
      await fetchOrder(
        1,
        event.target.value,
        businessDetails.id,
        search,
        paymentState.value,
        false
      );
    } else if (active === 3) {
      await fetchTransferRequest(
        1,
        event.target.value,
        businessDetails.id,
        transferPaymentState.value,
        false
      );
    } else {
      await fetchPayment(1, event.target.value, businessDetails.id, search);
    }
    setLoading(false);
  };
  const handleSelectAll = async () => {
    if (selected) {
      setSelectLoading(true);
      setSelectedData([]);
      setSelectedId([]);
      setSelected(!selected);
      setSelectLoading(false);
    } else {
      setSelectLoading(true);
      await fetchOrder(
        page,
        pageLimit,
        businessDetails.id,
        search,
        paymentState.value,
        true
      );
      setSelected(!selected);
      setSelectLoading(false);
    }
  };
  const handleSelectAllTransfer = async () => {
    if (selected) {
      setSelectLoading(true);
      setSelectedData([]);
      setSelectedId([]);
      setSelected(!selected);
      setSelectLoading(false);
    } else {
      setSelectLoading(true);
      await fetchTransferRequest(
        page,
        pageLimit,
        businessDetails.id,
        transferPaymentState.value,
        true
      );
      setSelected(!selected);
      setSelectLoading(false);
    }
  };

  const handleChecked = (id: string, data: any) => {
    let temp = [...selectedId];
    let selectedTemp = [...selectedData];
    if (temp.length) {
      if (temp.includes(id)) {
        temp = temp.filter((item) => item != id);
        selectedTemp = selectedTemp.filter((item) => item._id !== id);
      } else {
        selectedTemp.push(data);
        temp.push(id);
      }
    } else {
      selectedTemp.push(data);
      temp.push(id);
    }
    setSelectedData(selectedTemp);
    setSelectedId(temp);
    setSelected(temp.length == totalRecords);
  };
  console.log(localStorage.getItem("businessId"));
  console.log("selected", selected);

  const ordersColumns: ColumnDef<any>[] = [
    {
      header: () => (
        <div className="form-check form-check-custom form-check-solid">
          {paymentState.value === SellerPaymentUnPaid &&
          Method.hasPermission(PermissionModule.PaymentToVendors, Permissions.Add, currentUser) ? (
            <input
              className="form-check-input h-30px w-30px me-2"
              type="checkbox"
              value="1"
              checked={
                selectLoading || (selectedId.length == totalRecords && selected)
              }
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                handleSelectAll();
              }}
              disabled={
                !Method.hasPermission(PermissionModule.PaymentToVendors, Permissions.Add, currentUser)
              }
            />
          ) : (
            <></>
          )}
          Date
        </div>
      ),
      accessorKey: "_createdAt",
      cell: ({ row }) => {
        const paymentVal = row.original;
        return (
          <div className="form-check form-check-custom form-check-solid">
            {paymentState.value === SellerPaymentUnPaid &&
            Method.hasPermission(PermissionModule.PaymentToVendors, Permissions.Add, currentUser) ? (
              <input
                className="form-check-input h-30px w-30px"
                type="checkbox"
                value="1"
                checked={selectedId.includes(paymentVal._id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => {
                  handleChecked(paymentVal._id, paymentVal);
                }}
              />
            ) : (
              <></>
            )}
            <div className="d-flex align-items-start flex-column ms-2">
              <div className="d-flex flex-row">
                <span className="text-dark d-block">
                  {Method.convertDateToDDMMYYYYHHMM(paymentVal._createdAt, "-")}
                </span>
              </div>
              <span className="text-gray d-block">{paymentVal.refKey}</span>
            </div>
          </div>
        );
      },
    },
    {
      header: "Supplier name",
      cell: ({ row }) => row.original.business.ownerName,
    },
    {
      header: "Supplier group",
      cell: ({ row }) =>
        row.original.supplierGroup?.name ? (
          <span className="badge badge-light-primary fs-7 fw-bold">{row.original.supplierGroup.name}</span>
        ) : (
          <span className="text-gray-600 fs-7">No group</span>
        ),
    },
    {
      header: "Quantity",
      cell: ({ row }) => poQuantityText(row.original),
    },
    {
      header: "Total amount",
      cell: ({ row }) =>
        `TSh ${Method.getGeneralizedAmount(parseInt(row.original.charge))}`,
    },
    {
      header: "Status",
      cell: ({ row }) => {
        const paymentVal = row.original;
        const payout = paymentVal?.businesses?.[0]?.payout;
        return payout ? (
          <>
            {payout.paymentMethod === Credit ? (
              <span className="badge bg-warning fs-14 fw-600 text-dark p-3 px-4">
                Credit
              </span>
            ) : (
              <>
                {payout.completedBy?.user?.reference === currentUser?._id ? (
                  <span className="badge bg-e5f6de fs-14 fw-600 text-dark p-3 px-4">
                    Paid by you
                  </span>
                ) : (
                  <span className="badge bg-e5f6de fs-14 fw-600 text-dark p-3 px-4">
                    {payout.completedBy?.user?.name || "Paid"}
                  </span>
                )}
              </>
            )}
          </>
        ) : (
          <span className="badge bg-fff4d9 fs-14 fw-600 text-dark p-3 px-4">
            Not paid
          </span>
        );
      },
    },
  ];

  const paymentHistoryColumns: ColumnDef<any>[] = [
    {
      header: "Date & time",
      cell: ({ row }) => (
        <span className="text-dark d-block">
          {Method.convertDateToDDMMYYYYHHMM(row.original._createdAt, "-")}
        </span>
      ),
    },
    {
      header: "Total amount paid",
      cell: ({ row }) =>
        "TSh " + Method.getGeneralizedAmount(row.original.totalAmount),
    },
    {
      header: "Payment mode",
      cell: ({ row }) => (
        <div className="d-flex justify-content-center w-160px align-items-center bg-light p-3 pills-radius">
          <span className="fw-600 text-black fs-16">
            {row.original.paymentMethod === BankPayment ? "Bank transfer" : ""}{" "}
            {row.original.paymentMethod === TigoPesaPayment ? "Tigo pesa" : ""}{" "}
            {row.original.paymentMethod === Credit ? "Credit" : ""}{" "}
          </span>
        </div>
      ),
    },
    {
      header: "Transaction details",
      cell: ({ row }) => (row.original.paymentRef ? row.original.paymentRef : "-"),
    },
    {
      header: "Total purchaseOrders",
      cell: ({ row }) =>
        row.original.totalOrders +
        (row.original.isOrder
          ? row.original.totalOrders === 1
            ? " Order"
            : " Orders"
          : row.original.totalOrders === 1
          ? " Transfer"
          : " Transfers"),
    },
  ];

  return (
    <>
      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          onHide={() => setShowPermissionModal(false)}
          moduleName={"seller"}
        />
      )}
      {showOrderPermissionModal && (
        <PermissionModal
          show={showOrderPermissionModal}
          onHide={() => setShowOrderPermissionModal(false)}
          moduleName={"orders & delivery"}
        />
      )}
      <div className="p-5">
        <h1 className="fs-22 fw-bolder">Vendor finance details</h1>
        {businessDetails && Object.keys(businessDetails).length ? (
          <>
            <Card className="border-r10px mt-7">
              <Card.Body className="p-7">
                <Row className="align-items-center g-5">
                  <Col xs="auto">
                    <div className="symbol symbol-50px border">
                      <img
                        src={businessDetails.image}
                        className="img-fluid object-fit-contain"
                        alt=""
                      />
                    </div>
                  </Col>
                  <Col xs className="ps-lg-0">
                    <div className="fs-20 fw-bolder">
                      {businessDetails.ownerName}
                      {businessDetails.KYC.status === Accepted - 1 ? (
                        <img
                          className="ms-2 mb-1"
                          width={24}
                          height={24}
                          src={VerifiedPro}
                          alt={businessDetails.name}
                        />
                      ) : (
                        <></>
                      )}
                      {businessDetails.KYC.status === All - 1 ||
                      businessDetails.KYC.status === NewRequests - 1 ||
                      businessDetails.KYC.status === UnderReview - 1 ? (
                        <>
                          <span className="bage badge-light-warning text-dark border-r5px fs-14 fw-600 ms-2 p-2">
                            Verification pending
                          </span>
                        </>
                      ) : (
                        <></>
                      )}
                      {businessDetails.KYC.status === Rejected - 1 ? (
                        <>
                          <span className="bage badge-light-danger text-dark border-r5px fs-14 fw-600 ms-2 p-2">
                            Rejected
                          </span>
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </Col>
                  <Col sm="auto" className="text-end">
                    {Method.hasPermission(
                      PaymentToSellers,
                      View,
                      currentUser
                    ) && Method.hasPermission(AllSeller, View, currentUser) ? (
                      <Button
                        variant="link"
                        className="btn-flush fs-16 fw-bold text-primary"
                        onClick={() => {
                          navigateTo("/UProcure/vendors/vendor-profile", {
                            state: {
                              id: businessDetails.id,
                              moduleName: PaymentToSellers,
                            },
                          });
                        }}
                      >
                        View vendor profile
                      </Button>
                    ) : (
                      <></>
                    )}
                    {Method.hasPermission(
                      PaymentToSellers,
                      View,
                      currentUser
                    ) && !Method.hasPermission(AllSeller, View, currentUser) ? (
                      <Button
                        variant="link"
                        className="btn-flush fs-16 fw-bold text-primary"
                        onClick={() => {
                          setShowPermissionModal(true);
                        }}
                      >
                        View seller profile
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        ) : (
          <></>
        )}
        <div className="custom-tabContainer mt-7">
          <Tab.Container
            id="left-tabs-example"
            defaultActiveKey={active}
            onSelect={handleSelect}
          >
            <Row className="variant-categories g-5">
              <Col lg>
                <div className="bg-light border border-r8px p-3 px-md-6 w-fit-content">
                  <Nav variant="pills">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="1"
                        disabled={fetchLoading}
                        onClick={() => {
                          handleActive(1);
                        }}
                        active={active === 1}
                      >
                        Purchase orders{" "}
                        {active === 1 ? `(${totalRecords})` : ""}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="2"
                        disabled={fetchLoading}
                        onClick={() => {
                          handleActive(2);
                        }}
                        active={active === 2}
                      >
                        Payment history{active === 2 ? `(${totalRecords})` : ""}
                      </Nav.Link>
                    </Nav.Item>
                    {/* <Nav.Item>
                      <Nav.Link
                        eventKey="3"
                        disabled={fetchLoading}
                        onClick={() => {
                          handleActive(3);
                        }}
                        active={active === 3}
                      >
                        Inter transfer request
                        {active === 3 ? `(${totalRecords})` : ""}
                      </Nav.Link>
                    </Nav.Item> */}
                  </Nav>
                </div>
              </Col>
              {active.toString() === "1" || active.toString() === "3" ? (
                <Col md="auto">
                  <Row className="align-items-center g-5">
                    <Col sm="auto">
                      <label htmlFor="" className="fs-16 fw-500">
                        Filter orders
                      </label>
                    </Col>
                    <Col xs>
                      <div className="min-w-lg-198px">
                        <CustomSelect
                          disabled={fetchLoading || loading || selectLoading}
                          options={paymentStatusJSON}
                          value={
                            active.toString() === "1"
                              ? paymentState
                              : transferPaymentState
                          }
                          defaultValue={
                            active.toString() === "1"
                              ? paymentState
                              : transferPaymentState
                          }
                          onChange={(event: any) => {
                            if (active.toString() === "1") {
                              handleChange(event);
                            } else if (active.toString() === "3") {
                              handleChangeTransferFilter(event);
                            }
                          }}
                        />
                      </div>
                    </Col>
                    {active.toString() === "1" && groupOptions.length > 0 ? (
                      <Col xs data-testid="payment-group-filter">
                        <div className="min-w-lg-198px">
                          <CustomSelect
                            disabled={fetchLoading || loading || selectLoading}
                            options={groupSelectOptions}
                            value={groupSelectOptions.find((o) => o.value === groupFilter) || groupSelectOptions[0]}
                            onChange={(event: any) => handleGroupFilter(event)}
                          />
                        </div>
                      </Col>
                    ) : (
                      <></>
                    )}

                    {paymentState.value === SellerPaymentUnPaid ||
                    transferPaymentState.value === SellerPaymentUnPaid ? (
                      <>
                        {selectedData.length && Method.hasPermission(PermissionModule.PaymentToVendors,Permissions.Add,currentUser) ? (
                          <Col xs="auto">
                            {Method.hasPermission(
                              PermissionModule.PaymentToVendors,
                              Add,
                              currentUser
                            ) ? (
                              <Button
                                size="lg"
                                className="uw-btn uw-btn-primary uw-btn-lg min-h-60px"
                                onClick={() =>
                                  mixesGroups(selectedData)
                                    ? errorToast("The selected purchase orders belong to different supplier groups. Pay one group at a time (each group has its own bank account).")
                                    : navigateTo("make-payment", {
                                    state: {
                                      ...businessDetails,
                                      data: selectedData,
                                      id: businessDetails.id,
                                      ownerName: businessDetails.ownerName,
                                      name: businessDetails.ownerName,
                                      image: businessDetails.image,
                                      KYC: businessDetails.KYC,
                                      selectedId: selectedId,
                                      moduleKey:
                                        active.toString() === "1"
                                          ? 1
                                          : active.toString() === "3"
                                          ? 2
                                          : undefined,
                                    },
                                  })
                                }
                              >
                                Make a payment of {selectedData.length} orders
                              </Button>
                            ) : (
                              <></>
                            )}
                            {/* <Button size='lg' className='min-h-60px'>Make a payment of 4 orders</Button> */}
                          </Col>
                        ) : (
                          <></>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </Row>
                </Col>
              ) : (
                <></>
              )}
              <Tab.Content>
                {/* ORDERS */}
                <Tab.Pane eventKey="1" active={active.toString() === "1"}>
                  <Card className="border border-r10px">
                    <Card.Body className="pt-5">
                      <CustomTable
                        data={paymentData}
                        columns={ordersColumns}
                        loading={fetchLoading || loading || selectLoading}
                        onRowClick={
                          canViewOrderDetails ? goToOrderDetails : undefined
                        }
                      />
                    </Card.Body>
                  </Card>
                  {!fetchLoading ? (
                    <>
                      {paymentData.length ? (
                        <Pagination
                          totalRecords={totalRecords}
                          currentPage={page}
                          handleCurrentPage={(event: any) => {
                            handleCurrentPage(event);
                          }}
                          handleNextPage={(event: any) => {
                            handleNextPage(event);
                          }}
                          handlePreviousPage={(event: any) => {
                            handlePreviousPage(event);
                          }}
                          pageLimit={pageLimit}
                          handlePageLimit={(event: any) => {
                            handlePageLimit(event);
                          }}
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Tab.Pane>
                {/* PAYMENT HISTORY */}
                <Tab.Pane eventKey="2" active={active.toString() === "2"}>
                  <Card className="border border-r10px">
                    <Card.Body className="p-0">
                      <CustomTable
                        data={paymentData}
                        columns={paymentHistoryColumns}
                        loading={fetchLoading}
                        onRowClick={
                          canViewPaymentDetails ? goToPaymentDetails : undefined
                        }
                      />
                    </Card.Body>
                  </Card>
                  {!fetchLoading ? (
                    <>
                      {paymentData.length ? (
                        <Pagination
                          totalRecords={totalRecords}
                          currentPage={page}
                          handleCurrentPage={(event: any) => {
                            handleCurrentPage(event);
                          }}
                          handleNextPage={(event: any) => {
                            handleNextPage(event);
                          }}
                          handlePreviousPage={(event: any) => {
                            handlePreviousPage(event);
                          }}
                          pageLimit={pageLimit}
                          handlePageLimit={(event: any) => {
                            handlePageLimit(event);
                          }}
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Tab.Pane>
                {/* Inter Transfer Request */}
                <Tab.Pane eventKey="3" active={active.toString() === "3"}>
                  <Card className="border border-r10px">
                    <Card.Body className="pt-5">
                      <div className="table-responsive">
                        <table className="table table-row-bordered align-middle gs-0 gy-4 my-0">
                          <thead>
                            <tr className="fs-16 fw-600 align-middle">
                              <th className="min-w-150px">
                                <div className="form-check form-check-custom form-check-solid">
                                  {transferPaymentState.value ===
                                  SellerPaymentUnPaid ? (
                                    <>
                                      {Method.hasPermission(
                                        PermissionModule.PaymentToVendors,
                                        Add,
                                        currentUser
                                      ) ? (
                                        <input
                                          className="form-check-input h-30px w-30px me-2"
                                          type="checkbox"
                                          value="1"
                                          checked={
                                            selectLoading ||
                                            (selectedId.length ==
                                              totalRecords &&
                                              selected)
                                          }
                                          onChange={(event: any) => {
                                            handleSelectAllTransfer();
                                          }}
                                          disabled={
                                            !Method.hasPermission(
                                              PaymentToSellers,
                                              Add,
                                              currentUser
                                            )
                                          }
                                        />
                                      ) : (
                                        <></>
                                      )}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                  Transfer Date & ID
                                </div>
                              </th>
                              <th className="min-w-100px">Quantity</th>
                              <th className="min-w-120px">Total amount</th>
                              <th className="min-w-120px">Status</th>
                              <th className="min-w-150px text-end"></th>
                            </tr>
                          </thead>
                          <tbody className="fs-15 fw-600">
                            <>
                              {fetchLoading || loading ? (
                                <>
                                  {" "}
                                  <tr>
                                    <td colSpan={6}>
                                      <div className="d-flex justify-content-center">
                                        <Loader
                                          loading={fetchLoading || loading}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                </>
                              ) : (
                                <>
                                  {active.toString() === "3" &&
                                  transferData.length ? (
                                    <>
                                      {selectLoading ? (
                                        <tr>
                                          <td colSpan={6}>
                                            <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3 fs-20 text-dark fw-600">
                                              Please wait while processing...
                                            </div>
                                          </td>
                                        </tr>
                                      ) : (
                                        <>
                                          {transferData?.map(
                                            (
                                              paymentVal: any,
                                              index: number
                                            ) => (
                                              <>
                                                <tr key={index}>
                                                  <td>
                                                    <div className="form-check form-check-custom form-check-solid">
                                                      {transferPaymentState.value ===
                                                      SellerPaymentUnPaid ? (
                                                        <>
                                                          {Method.hasPermission(
                                                            PermissionModule.PaymentToVendors,
                                                            Add,
                                                            currentUser
                                                          ) ? (
                                                            <input
                                                              className="form-check-input h-30px w-30px"
                                                              type="checkbox"
                                                              value="1"
                                                              checked={selectedId.includes(
                                                                paymentVal._id
                                                              )}
                                                              onChange={() => {
                                                                handleChecked(
                                                                  paymentVal._id,
                                                                  paymentVal
                                                                );
                                                              }}
                                                            />
                                                          ) : (
                                                            <></>
                                                          )}
                                                        </>
                                                      ) : (
                                                        <></>
                                                      )}
                                                      <div className="d-flex align-items-start flex-column ms-2">
                                                        <div className="d-flex flex-row">
                                                          <span className="text-dark d-block">
                                                            {Method.convertDateToDDMMYYYYHHMM(
                                                              paymentVal._createdAt,
                                                              "-"
                                                            )}
                                                          </span>
                                                        </div>
                                                        <span className="text-gray d-block">
                                                          {paymentVal.refKey}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </td>
                                                  <td>
                                                    {paymentVal.transferredQuantities +
                                                      " items"}
                                                  </td>
                                                  <td>
                                                    {`TSh ${Method.getGeneralizedAmount(
                                                      parseInt(
                                                        paymentVal.variants.reduce(
                                                          (p: any, c: any) => {
                                                            return (
                                                              p + c.totalAmount
                                                            );
                                                          },
                                                          0
                                                        )
                                                      ) -
                                                        paymentVal.commission
                                                          .charge
                                                    )}`}
                                                  </td>
                                                  <td>
                                                    {paymentVal.businesses[0]
                                                      .payout ? (
                                                      <>
                                                        {paymentVal
                                                          .businesses[0].payout
                                                          .completedBy
                                                          .paymentMethod ===
                                                        Credit ? (
                                                          <span className="badge bg-warning fs-14 fw-600 text-dark p-3 px-4">
                                                            Credit Payment
                                                          </span>
                                                        ) : (
                                                          <>
                                                            {paymentVal
                                                              .businesses[0]
                                                              .payout
                                                              .completedBy.user
                                                              .reference ===
                                                            currentUser?._id ? (
                                                              <span className="badge bg-e5f6de fs-14 fw-600 text-dark p-3 px-4">
                                                                Paid by you
                                                              </span>
                                                            ) : (
                                                              <span className="badge bg-e5f6de fs-14 fw-600 text-dark p-3 px-4">
                                                                {
                                                                  paymentVal
                                                                    .businesses[0]
                                                                    .payout
                                                                    .completedBy
                                                                    .user.name
                                                                }
                                                              </span>
                                                            )}
                                                          </>
                                                        )}{" "}
                                                      </>
                                                    ) : (
                                                      <span className="badge bg-fff4d9 fs-14 fw-600 text-dark p-3 px-4">
                                                        Not paid
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    <Button className="uw-btn uw-btn-primary"
                                                      onClick={() => {
                                                        let currentScrollY =
                                                          window.scrollY;
                                                        setKey(
                                                          listVendorFinanceDetails.scrollPosition,
                                                          currentScrollY
                                                        );
                                                        localStorage.setItem(
                                                          "businessId",
                                                          state?.id
                                                        );
                                                        navigateTo(
                                                          "/inter-transfer-request/inter-transfer-detail",
                                                          {
                                                            state: {
                                                              id: paymentVal._id,
                                                            },
                                                          }
                                                        );
                                                      }}
                                                    >
                                                      View details
                                                    </Button>
                                                  </td>
                                                </tr>
                                              </>
                                            )
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <tr>
                                        <td colSpan={6}>
                                          <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                            No Data found
                                          </div>
                                        </td>
                                      </tr>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>
                  {!fetchLoading ? (
                    <>
                      {transferData.length ? (
                        <Pagination
                          totalRecords={totalRecords}
                          currentPage={page}
                          handleCurrentPage={(event: any) => {
                            handleCurrentPage(event);
                          }}
                          handleNextPage={(event: any) => {
                            handleNextPage(event);
                          }}
                          handlePreviousPage={(event: any) => {
                            handlePreviousPage(event);
                          }}
                          pageLimit={pageLimit}
                          handlePageLimit={(event: any) => {
                            handlePageLimit(event);
                          }}
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Row>
          </Tab.Container>
        </div>
      </div>
    </>
  );
};
export default VendorFinanceDetails;
